<template>
  <div ref="viewport" class="ap-viewport" @scroll="onScroll">
    <table class="ap-table">
      <thead>
        <tr>
          <th class="ap-corner">
            <v-progress-linear
              :style="{ opacity: !tasksLoaded ? 1 : 0 }"
              color="primary"
              height="3"
              indeterminate
              absolute
              bottom
            />
          </th>
          <th
            v-for="location in locations"
            :key="location.id"
            class="ap-header-cell"
          >
            <div style="font-size: 12px; white-space: nowrap">
              {{ location.assembly_location }}
            </div>
            <div class="font-italic" style="font-size: 12px; white-space: nowrap">
              {{ location.description }}
            </div>
          </th>
        </tr>
      </thead>
      <tbody>
        <tr v-if="topSpacerHeight > 0" class="ap-spacer">
          <td :colspan="locations.length + 1" :style="{ height: topSpacerHeight + 'px' }" />
        </tr>
        <tr
          v-for="module in visibleModules"
          :key="module.id"
          class="ap-row"
          :class="{
            'ap-row--active': module === selectedModule,
            'ap-row--disabled': !tasksLoaded,
          }"
        >
          <td
            class="ap-row-header"
            :class="{ 'ap-row-header--active': module === selectedModule }"
            @click="tasksLoaded && $emit('select-module', module)"
          >
            <div class="font-weight-bold" style="font-size: 12px">
              {{ module.build_nr || '-' }}
            </div>
            <div class="d-flex justify-space-between" style="font-size: 12px">
              <span>{{ module.module_id }}</span>
              <span class="font-italic ml-1 text-no-wrap">{{ module.module_type }}</span>
            </div>
          </td>
          <td
            v-for="location in locations"
            :key="location.id"
            class="ap-cell"
          >
            <i
              v-if="getLocationStatusIcon(module, location)"
              class="v-icon notranslate mdi theme--light"
              :class="getLocationStatusIcon(module, location)"
              :style="{ color: getLocationStatusColor(module, location) }"
            />
          </td>
        </tr>
        <tr v-if="bottomSpacerHeight > 0" class="ap-spacer">
          <td :colspan="locations.length + 1" :style="{ height: bottomSpacerHeight + 'px' }" />
        </tr>
      </tbody>
    </table>
  </div>
</template>

<script>
const ROW_HEIGHT = 48;
const OVERSCAN = 5;

export default {
  name: 'DhmeAssemblyPreparationsGrid',
  props: {
    modules: {
      type: Array,
      required: true,
    },
    locations: {
      type: Array,
      required: true,
    },
    statusMap: {
      type: Map,
      required: true,
    },
    selectedModule: {
      default: null,
    },
    tasksLoaded: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      scrollTop: 0,
      viewportHeight: 0,
    };
  },
  computed: {
    visibleStart() {
      return Math.max(0, Math.floor(this.scrollTop / ROW_HEIGHT) - OVERSCAN);
    },
    visibleEnd() {
      return Math.min(
        this.modules.length,
        Math.ceil((this.scrollTop + this.viewportHeight) / ROW_HEIGHT) + OVERSCAN
      );
    },
    visibleModules() {
      return this.modules.slice(this.visibleStart, this.visibleEnd);
    },
    topSpacerHeight() {
      return this.visibleStart * ROW_HEIGHT;
    },
    bottomSpacerHeight() {
      return (this.modules.length - this.visibleEnd) * ROW_HEIGHT;
    },
  },
  mounted() {
    this.viewportHeight = this.$refs.viewport.clientHeight;
    this._resizeObserver = new ResizeObserver(() => {
      this.viewportHeight = this.$refs.viewport.clientHeight;
    });
    this._resizeObserver.observe(this.$refs.viewport);
  },
  beforeDestroy() {
    if (this._resizeObserver) {
      this._resizeObserver.disconnect();
    }
  },
  methods: {
    onScroll() {
      this.scrollTop = this.$refs.viewport.scrollTop;
    },
    getLocationStatusIcon(module, location) {
      const record = this.statusMap.get(`${module.module_id}::${location.id}`);
      if (record) {
        switch (record.status) {
          case 'success':
            return 'mdi-check-circle-outline';
          case 'alert':
            return 'mdi-alert-circle-outline';
          case 'reopened':
            return 'mdi-restore';
        }
      }
    },
    getLocationStatusColor(module, location) {
      const record = this.statusMap.get(`${module.module_id}::${location.id}`);
      if (record) {
        switch (record.status) {
          case 'success':
            return this.$vuetify.theme.themes.light.success;
          case 'alert':
            return this.$vuetify.theme.themes.light.info;
          case 'reopened':
            return this.$vuetify.theme.themes.light.warning;
        }
      }
    },
  },
};
</script>

<style scoped>
.ap-viewport {
  flex: 1;
  overflow: auto;
  /* Ensure the viewport fills its flex parent */
  min-height: 0;
}

.ap-table {
  border-collapse: collapse;
  /* width: max-content ensures the table always expands to its full natural width
     so the viewport scrolls horizontally instead of squishing columns */
  width: max-content;
}

/* ── Sticky header row ─────────────────────────────────────── */
thead th {
  position: sticky;
  top: 0;
  z-index: 3;
  background: white;
  border-bottom: 1px solid gainsboro;
  vertical-align: middle;
}

.ap-corner {
  position: sticky;
  left: 0;
  /* corner must be above both the sticky header AND sticky first column */
  z-index: 4 !important;
  width: 160px;
  min-width: 160px;
  height: 48px;
  padding: 4px 8px;
  background: white;
  border-right: 1px solid gainsboro;
  /* subtle right shadow to separate frozen column from scrolling content */
  box-shadow: 2px 0 4px -1px rgba(0, 0, 0, 0.08);
}

.ap-header-cell {
  width: 96px;
  min-width: 96px;
  height: 48px;
  padding: 4px;
  text-align: center;
  border-right: 1px solid gainsboro;
}

/* ── Sticky first column (row header) ─────────────────────── */
.ap-row-header {
  position: sticky;
  left: 0;
  z-index: 2;
  width: 160px;
  min-width: 160px;
  height: 48px;
  padding: 4px 8px;
  background: white;
  border-right: 1px solid gainsboro;
  border-bottom: 1px solid gainsboro;
  cursor: pointer;
  transition: background 200ms ease-out;
  box-shadow: 2px 0 4px -1px rgba(0, 0, 0, 0.08);
}

.ap-row-header:hover {
  background: #f2f2f2;
}

.ap-row-header--active {
  background: #f2f2f2 !important;
  border-left: 2px solid var(--v-primary-base);
}

/* ── Data cells ────────────────────────────────────────────── */
.ap-cell {
  width: 96px;
  min-width: 96px;
  height: 48px;
  text-align: center;
  vertical-align: middle;
  border-right: 1px solid gainsboro;
  border-bottom: 1px solid gainsboro;
  background: white;
}

/* ── Row states ────────────────────────────────────────────── */
.ap-row--disabled .ap-row-header {
  cursor: not-allowed;
  opacity: 0.5;
}

.ap-row--disabled .ap-cell {
  opacity: 0.5;
}

/* ── Spacer rows ───────────────────────────────────────────── */
.ap-spacer td {
  padding: 0;
  border: none;
}
</style>
